import { UserDTO } from '@sellsen/generated-sources';
import type { MenuProps } from 'antd';
import { Avatar, Dropdown, Grid, Layout, Menu, Space, theme, Tooltip } from 'antd';
import { ItemType } from 'antd/es/menu/interface';
import React, { useState } from 'react';

const { Header, Content } = Layout;

interface AppLayoutProps {
  children: React.ReactNode;
  onMenuSelect?: (key: string) => void;
  menuItems: MenuProps['items'];
  userMenuItems: MenuProps['items'];
  user?: UserDTO;
  brandTitle?: React.ReactNode;
  /** Kullanıcı menüsünün solunda duran ek üst çubuk içeriği (ör. global arama). */
  headerExtra?: React.ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  onMenuSelect,
  menuItems,
  userMenuItems,
  user,
  brandTitle = 'SELLSEN',
  headerExtra,
}) => {
  const [selectedKey, setSelectedKey] = useState('1-1');

  const {
    token: { colorBgContainer },
  } = theme.useToken();
  // İlk render'da breakpoint'ler bilinmez ({}); geniş kabul edip adın zıplamasını önlüyoruz.
  const screens = Grid.useBreakpoint();
  const showUserName = screens.xl !== false;

  const displayName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.username;

  const handleMenuClick = ({ key }: { key: string }) => {
    setSelectedKey(key);

    onMenuSelect?.(key);
  };

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return (
        (user.firstName[0] || '').toUpperCase() +
        (user.lastName[0] || '').toUpperCase()
      );
    }
    if (user?.username) {
      return user.username.slice(0, 2).toUpperCase();
    }
    return 'AB'; // fallback if no data
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header
        style={{
          position: 'fixed',
          top: 0,
          zIndex: 1000,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          height: 64,
          lineHeight: 'normal',
          background: colorBgContainer,
          borderBottom: '1px solid #f0f0f0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        <div
          style={{
            display: 'flex',
            flex: '1 1 auto',
            minWidth: 0,
            alignItems: 'center',
          }}
        >
          <div
            style={{
              fontSize: typeof brandTitle === 'string' ? '20px' : undefined,
              fontWeight: 'bold',
              color: '#1890ff',
              marginRight: '24px',
              flexShrink: 0,
              lineHeight: 1.2,
            }}
          >
            {brandTitle}
          </div>
          <Menu
            mode="horizontal"
            selectedKeys={[selectedKey]}
            onClick={handleMenuClick}
            style={{
              flex: 1,
              // Flex öğesinin varsayılan min-width:auto'su menünün daralmasını engelliyordu;
              // daralamayan menü sağ kümenin üstüne taşıyor, antd'nin '···' taşma menüsü de
              // hiç devreye girmiyordu.
              minWidth: 0,
              border: 'none',
              background: 'transparent',
            }}
            items={menuItems}
          />
        </div>

        <Space size="small" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
          {headerExtra}
          <Dropdown
            menu={{
              items: userMenuItems?.map((item) => ({
                ...item,
                onClick: () => {
                  handleMenuClick({ key: item?.key as string });
                },
              })) as ItemType[],
            }}
            placement="bottomRight"
            arrow
          >
            <Space style={{ cursor: 'pointer' }}>
              {/* Ad gizlendiğinde kimlik hover'da hâlâ görünsün. */}
              <Tooltip title={showUserName ? undefined : displayName}>
                <Avatar size="default" src={user?.imageUrl} alt="User Avatar">
                  {getInitials()}
                </Avatar>
              </Tooltip>
              {showUserName ? (
                <span style={{ color: '#666', fontWeight: '500' }}>
                  {displayName}
                </span>
              ) : null}
            </Space>
          </Dropdown>
        </Space>
      </Header>

      <Layout style={{ marginTop: 64 }}>
        <Content
          style={{
            // Sabit 122px kenar boşluğu dar ekranda içeriği boğuyordu; 1920'de aynı genişlik
            // (1680) korunur, altında boşluk 24px'e iner.
            width: '100%',
            maxWidth: 1680,
            margin: '24px auto',
            padding: '24px',
            minHeight: 'calc(100vh - 112px)',
            // background: colorBgContainer,
            // borderRadius: borderRadiusLG,
            // boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
